import { Link } from "react-router-dom";
import { RefreshCw, MailWarning } from "lucide-react";
import { useSetTopbarCrumbs } from "@marekl/adminer/shell";
import { Page, Card, Table, Button, Alert, Badge, Text, EmptyState, type Column } from "@marekl/adminer/components";
import { formatDateTime } from "@marekl/adminer/format";

import { useEmailProblems, EMAIL_PROBLEM_LABELS, type EmailProblem } from "../../lib/queries/emails";
import { useAuth } from "../../auth/useAuth";

/**
 * The certificates whose e-mail did not arrive.
 *
 * Not a queue — kartis keeps none. Sending is synchronous, so nothing here is
 * waiting for a machine: every row is a customer who needs a person to resend
 * their certificate, usually to a corrected address. On a good day the page
 * is empty.
 */
export function EmailProblemsPage() {
  useSetTopbarCrumbs([{ label: "Nedoručené e-maily" }]);
  const { user } = useAuth();
  const isSuperadmin = user?.role === "superadmin";
  const result = useEmailProblems();
  const items = result.data?.items ?? [];

  const columns: Column<EmailProblem>[] = [
    {
      key: "certificate",
      header: "Certifikát",
      primary: true,
      render: (row) => (
        <Link to={`/certificates?code=${encodeURIComponent(row.redemption_code)}`}>{row.redemption_code}</Link>
      ),
    },
    { key: "customer", header: "Zákazník", render: (row) => row.customer_name },
    {
      key: "to",
      header: "Adresa",
      render: (row) => <Text variant="mono">{row.to}</Text>,
    },
    {
      key: "reason",
      header: "Důvod",
      render: (row) => (
        <Badge variant={row.reason === "blocked" ? "warn" : "err"}>
          {EMAIL_PROBLEM_LABELS[row.reason] ?? row.reason}
        </Badge>
      ),
    },
    {
      key: "detail",
      header: "Podrobnost",
      render: (row) => (row.detail ? <Text variant="muted">{row.detail}</Text> : <Text variant="muted">—</Text>),
    },
    {
      key: "at",
      header: "Kdy",
      render: (row) => <Text variant="mono">{formatDateTime(row.at)}</Text>,
    },
  ];

  return (
    <Page.Content>
      <Page.Header
        title="Nedoručené e-maily"
        subtitle="certifikáty, které je potřeba poslat znovu"
        subtitleVariant="tagline"
        actions={
          <Button variant="default" onClick={() => void result.refetch()} loading={result.isFetching}>
            <RefreshCw size={16} aria-hidden="true" />
            Obnovit
          </Button>
        }
      />

      {result.data?.letterman_unavailable && (
        <Alert variant="warn">
          <strong>Letterman se nepodařilo přečíst.</strong> Seznam je neúplný — chybí e-maily,
          které Letterman převzal a nedoručil. Zkuste to za chvíli znovu.
        </Alert>
      )}

      <Card bodyPadding="none">
        <Table
          columns={columns}
          rows={items}
          rowKey={(row) => `${row.certificate_id}-${row.message_id ?? row.at}`}
          cards
          isLoading={result.isLoading}
          error={result.isError ? result.error : undefined}
          onRetry={() => void result.refetch()}
          // Never say "everything is delivered" while the answer is unknown.
          empty={
            result.isLoading ? undefined : (
              <EmptyState
                icon={MailWarning}
                title="Žádné nedoručené e-maily"
                description="Každý certifikát z posledních 90 dnů, který měl odejít, odešel."
              />
            )
          }
        />
      </Card>

      {isSuperadmin && items.length > 0 && (
        <Text variant="muted">
          Podrobnosti o doručování jsou v Lettermanu — otevřete certifikát a jeho e-maily.
        </Text>
      )}
    </Page.Content>
  );
}
